import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, OnboardingContainer, UploadProfilePhotoInput } from '../../../components';
import { Content, Form, StyledButton, Subtitle, Title } from './TelegramInfoPage.styles';
import { UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import CropImageModal from '../../../components/modals/CropImageModal/CropImageModal';
import { useDispatch, useSelector } from 'react-redux';
import { updateTelegramInfo } from '../../../store/slices/influencerRequest/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { selectInfluencerRequestActionsPending } from '../../../store/slices/influencerRequest/slice';

export const updateBasicInfoFormSchema = yup
  .object({
    name: yup.string().trim().required('Name is required').max(255, 'Max length reached'),
    username: yup.string().trim().required('Username is required').max(255, 'Max length reached'),
    profilePhoto: yup.mixed().required('Profile photo is required'),
  })
  .required();

const TelegramInfoPage = () => {
  const dispatch = useDispatch();
  const { updateTelegramInfo: updateTelegramInfoPending } = useSelector(selectInfluencerRequestActionsPending);
  const cropImageRef = useRef();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      username: '',
      profilePhoto: null,
    },
    delayError: 300,
    resolver: yupResolver(updateBasicInfoFormSchema),
  });

  const watchProfilePhoto = watch('profilePhoto');

  useEffect(() => {
    if (watchProfilePhoto) {
      if (typeof watchProfilePhoto !== 'string') {
        cropImageRef.current.show();
      }
    }
  }, [watchProfilePhoto]);

  const onSubmit = data => {
    dispatch(updateTelegramInfo(data))
      .unwrap()
      .then(() => {
        notifyInfo('Telegram info updated successfully');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <OnboardingContainer>
      <Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Title>Telegram</Title>
          <Subtitle>Customize your profile</Subtitle>
          <Controller
            name="profilePhoto"
            control={control}
            render={({ field }) => {
              return (
                <UploadProfilePhotoInput
                  label="Profile photo"
                  value={field.value}
                  onChange={field.onChange}
                  type={UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO}
                  error={errors.profilePhoto?.message}
                />
              );
            }}
          />
          <Input label="Profile Name" placeholder="Enter name" {...register('name')} error={errors.name?.message} />
          <Input
            label="Username"
            placeholder="Enter username"
            {...register('username')}
            error={errors.username?.message}
          />
          <StyledButton title="Continue" isLoading={updateTelegramInfoPending} />
        </Form>
      </Content>
      <CropImageModal
        ref={cropImageRef}
        onClose={() => {
          setValue('profilePhoto', null);
          cropImageRef.current.hide();
        }}
        onComplete={croppedImage => {
          setValue('profilePhoto', croppedImage);
          cropImageRef.current.hide();
        }}
        originalImage={getValues('profilePhoto')}
      />
    </OnboardingContainer>
  );
};

export default TelegramInfoPage;
